'use client'

import { cn } from '@brew/ui/cn'
import { useUser } from '@clerk/nextjs'
import { Check, Copy } from 'lucide-react'
import { motion, type Variants } from 'motion/react'
import { useTranslations } from 'next-intl'
import { type ReactNode, useState } from 'react'
import { Empty } from '@/components/ui/empty'

const FORWARD_EMAIL = 'hello@email.brew.new'

/** The visitor's own address, set off inside the sentence. */
function ownAddress(chunks: ReactNode) {
  return <span className="font-medium text-foreground">{chunks}</span>
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

const itemVariants: Variants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  },
}

const imageContainerVariants: Variants = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
}

export function ForwardedEmptyState() {
  const t = useTranslations('templates')
  const { user } = useUser()
  const [isCopied, setIsCopied] = useState(false)
  const userEmail = user?.primaryEmailAddress?.emailAddress

  const handleCopy = async () => {
    await navigator.clipboard.writeText(FORWARD_EMAIL)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  return (
    <Empty className="text-wrap p-8 md:p-8">
      <motion.div
        className="relative w-full max-w-3xl overflow-hidden rounded-lg border bg-card text-center shadow-sm"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute top-0 left-1/2 -z-10 h-2/3 w-2/3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

        <motion.div
          variants={imageContainerVariants}
          className="relative aspect-video w-full overflow-hidden rounded-t-md bg-muted"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          >
            <source
              src="https://cdn.brew.new/videos/forward-email.mp4"
              type="video/mp4"
            />
          </video>
        </motion.div>

        <div className="flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="text-left">
            <motion.h2
              variants={itemVariants}
              className="font-semibold text-card-foreground text-lg tracking-tight"
            >
              {t('forwarded.title')}
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="mt-1 max-w-md text-muted-foreground text-sm"
            >
              {t.rich('forwarded.description', {
                hasAddress: String(Boolean(userEmail)),
                address: userEmail ?? '',
                email: ownAddress,
              })}
            </motion.p>
          </div>

          <motion.div variants={itemVariants} className="shrink-0 text-right">
            <p className="mb-1.5 text-muted-foreground text-xs">
              {t('forwarded.forwardTo')}
            </p>
            <button
              type="button"
              onClick={handleCopy}
              className="ml-auto flex w-fit items-center gap-3 rounded-lg bg-muted px-3 py-2 text-sm transition-colors hover:bg-muted/80"
            >
              <code className="font-mono text-foreground">{FORWARD_EMAIL}</code>
              <span className="relative h-4 w-4">
                <Copy
                  className={cn(
                    'absolute inset-0 h-4 w-4 text-muted-foreground transition-opacity duration-200',
                    isCopied ? 'opacity-0' : 'opacity-100'
                  )}
                />
                <Check
                  className={cn(
                    'absolute inset-0 h-4 w-4 text-green-500 transition-opacity duration-200',
                    isCopied ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </span>
            </button>
          </motion.div>
        </div>
      </motion.div>
    </Empty>
  )
}
